<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowLeft, FileText, Copy, Check, ExternalLink } from 'lucide-vue-next'
import type { BridgeOrganization } from '@/types/bridge'

const props = defineProps<{
  bridgeOrg: BridgeOrganization | null
  orgId: number
}>()

const emit = defineEmits<{
  (e: 'back'): void
}>()

const DEMO_TOS_URL = 'https://www.youtube.com/shorts/41iWg91yFv0'
const DEMO_KYB_URL = 'https://www.youtube.com/shorts/41iWg91yFv0'


const tosUrl = computed(() => props.bridgeOrg?.link?.tos_url ?? DEMO_TOS_URL)
const kybUrl = computed(() => props.bridgeOrg?.link?.kyc_url ?? DEMO_KYB_URL)

const copiedTos = ref(false)
const copiedKyb = ref(false)

const copyToClipboard = async (url: string, which: 'tos' | 'kyb') => {
  await navigator.clipboard.writeText(url)
  if (which === 'tos') {
    copiedTos.value = true
    setTimeout(() => { copiedTos.value = false }, 2000)
  } else {
    copiedKyb.value = true
    setTimeout(() => { copiedKyb.value = false }, 2000)
  }
}
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="px-4 py-3 border-b flex-shrink-0 flex items-center gap-2">
      <button @click="emit('back')" class="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
        <ArrowLeft class="h-4 w-4" />
      </button>
      <h2 class="font-semibold text-sm">KYB Onboarding</h2>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
      <div class="max-w-[600px] space-y-3">
      <p class="text-sm text-muted-foreground">
        Share these links with the organization's primary contact to complete onboarding.
      </p>
      <p class="text-sm text-muted-foreground">
        These have been emailed to the organization's admin contact.
      </p>

      <!-- ToS Link -->
      <div class="rounded-lg border bg-card p-4 space-y-3">
        <div class="flex items-center gap-2">
          <FileText class="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span class="text-sm font-medium">Terms of Service</span>
        </div>
        <p class="text-sm text-muted-foreground">
          The contact must review and accept Bridge's payment terms before KYB can begin.
        </p>
        <div class="flex items-center gap-2">
          <a
            :href="tosUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 text-xs font-mono text-blue-600 hover:underline truncate"
          >
            {{ tosUrl }}
          </a>
          <button
            @click="copyToClipboard(tosUrl, 'tos')"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Copy link"
          >
            <Check v-if="copiedTos" class="h-4 w-4 text-green-600" />
            <Copy v-else class="h-4 w-4" />
          </button>
          <a
            :href="tosUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Open link"
          >
            <ExternalLink class="h-4 w-4" />
          </a>
        </div>
      </div>

      <!-- KYB Link -->
      <div class="rounded-lg border bg-card p-4 space-y-3">
        <div class="flex items-center gap-2">
          <FileText class="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span class="text-sm font-medium">Business Verification (KYB)</span>
        </div>
        <p class="text-sm text-muted-foreground">
          Identity and business verification via Persona. Must be completed after ToS acceptance.
        </p>
        <div class="flex items-center gap-2">
          <a
            :href="kybUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 text-xs font-mono text-blue-600 hover:underline truncate"
          >
            {{ kybUrl }}
          </a>
          <button
            @click="copyToClipboard(kybUrl, 'kyb')"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Copy link"
          >
            <Check v-if="copiedKyb" class="h-4 w-4 text-green-600" />
            <Copy v-else class="h-4 w-4" />
          </button>
          <a
            :href="kybUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            title="Open link"
          >
            <ExternalLink class="h-4 w-4" />
          </a>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>
